import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import router from '@/common/router';
import type { BreadCrumbMenuInfo } from '@/common/components/molecules/breadCrumbs/breadCrumbs.types';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import type { LastSelectedInfo, OverviewType } from '@/database/utils/overview.types';
import { useDatabaseOverviewStore } from '@/database/stores/database-overview';
import { type InstanceGroupInfo } from '@/database/utils/pa/pa.types';
import { OVERVIEW_TYPE } from '@/database/utils/overview.define';
import { useDatabaseMetaStore } from '@/database/stores/database-meta';

export type InstanceType = OverviewType;

export const GNB_DB_LISTS = [
  BASE_MENU_VALUE.ORACLE,
  BASE_MENU_VALUE.SQLSERVER,
  BASE_MENU_VALUE.MYSQL,
  BASE_MENU_VALUE.POSTGRESQL,
  BASE_MENU_VALUE.REDIS,
  BASE_MENU_VALUE.CUBRID,
  BASE_MENU_VALUE.MONGODB,
  BASE_MENU_VALUE.TIBERO,
  BASE_MENU_VALUE.ALTIBASE,
  BASE_MENU_VALUE.CLICKHOUSE,
  BASE_MENU_VALUE.REDSHIFT,
  BASE_MENU_VALUE.SINGLESTORE,
] as const;

export interface CurrentInstance {
  multiView: {
    name: string;
    id: string;
  };
  singleView: {
    name: string;
    id: string;
  };
  currentDbInstanceType?: InstanceType;
}

export interface BreadCrumbItemInfo {
  id: string;
  name: string;
  type: (typeof GNB_DB_LISTS)[number];
}

export interface BreadCrumbInstanceInfo extends BreadCrumbItemInfo {
  children?: BreadCrumbItemInfo[];
}

const getEnabledInstances = (
  instances: InstanceGroupInfo['instances'],
): NonNullable<InstanceGroupInfo['instances']> => {
  if (!instances) {
    return [];
  }
  return instances.filter((instanceInfo) => !!instanceInfo.enabled);
};

const getConvertedInstances = (data: InstanceGroupInfo[]): BreadCrumbInstanceInfo[] => {
  return data
    .filter((groupInfo) => {
      const enabledInstances = getEnabledInstances(groupInfo.instances);
      return enabledInstances.length > 0;
    })
    .map((groupInfo) => {
      const enabledInstances = getEnabledInstances(groupInfo.instances);
      return {
        id: groupInfo.folderId ?? '',
        name: groupInfo.name ?? '',
        type: (enabledInstances[0]?.type || '') as BreadCrumbItemInfo['type'],
        children: enabledInstances.map((instanceInfo) => ({
          id: instanceInfo?.instanceId ?? '',
          name: instanceInfo?.alias || instanceInfo?.name || '',
          type: instanceInfo?.type as BreadCrumbItemInfo['type'],
        })),
      };
    });
};

export const useInstanceStore = defineStore('instance', () => {
  const currentInstance = ref<CurrentInstance>({
    multiView: {
      name: 'Multi View',
      id: '',
    },
    singleView: {
      name: 'Single View',
      id: '',
    },
    currentDbInstanceType: 'multiView',
  });

  /**
   * 1. breadCrumbs에 사용
   */
  const instances = computed<BreadCrumbInstanceInfo[]>(() => {
    const groupsData = useDatabaseMetaStore().getInstanceGroups();
    return getConvertedInstances(groupsData || []);
  });

  const findMultiView = (id?: string) => {
    return instances.value?.find((instance) => instance.children?.some((child) => child.id === id));
  };

  const setCurrentInstance = (instance: Partial<CurrentInstance>) => {
    currentInstance.value = {
      ...currentInstance.value,
      ...instance,
    };
  };

  /**
   * 1. breadCrumbs
   * 2. Maxgauge 모드 > GNB
   */

  const changeInstance = async (menu: BreadCrumbMenuInfo) => {
    try {
      const info =
        menu.instanceType === OVERVIEW_TYPE.MULTI_VIEW
          ? {
              folderId: menu.value,
            }
          : {
              folderId: findMultiView(menu.value)?.id,
              instanceId: menu.value,
            };

      const selectInfo = {
        dbType: menu.dbType,
        viewType: menu.instanceType,
        info,
      } as LastSelectedInfo;

      await useDatabaseOverviewStore().setLastOverviewInfo(selectInfo);
      await router.push({
        path: `/${menu.dbType}/${menu.instanceType}`,
        query: { id: menu.value },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const changeGNBInstance = (rootPath: string, instanceType: InstanceType) => {
    const [, routeRootPath, routeInstanceType] = router.currentRoute.value.path.split('/');
    if (routeRootPath === rootPath && routeInstanceType === instanceType) return;

    const findInstance = instances.value?.find((instance) => instance.type === rootPath);
    const findSingleView = findInstance?.children?.[0];

    let menuInfo: BreadCrumbMenuInfo = {
      dbType: rootPath,
      instanceType,
    };

    if (instanceType === 'multiView') {
      menuInfo = {
        ...menuInfo,
        text: findInstance?.name,
        value: findInstance?.id,
      };
    } else {
      menuInfo = {
        ...menuInfo,
        text: findSingleView?.name,
        value: findSingleView?.id,
      };
    }

    changeInstance(menuInfo);
  };

  return {
    instances,
    currentInstance,
    findMultiView,
    setCurrentInstance,
    changeInstance,
    changeGNBInstance,
  };
});
